<script setup>
import { computed, getCurrentInstance, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: [Array, Object],
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
const WAITING_ON_VALUES = ['agent', 'customer', 'partner', 'system', 'none'];
const DUE_IN_UNITS = ['minutes', 'hours', 'days', 'weeks'];
const IDEMPOTENCY_STRATEGIES = [
  'per_execution',
  'per_source',
  'per_contact',
  'per_conversation',
  'per_visit',
  'per_offer',
];
const CONTEXT_SELECTORS = ['contact', 'conversation', 'visit', 'offer'];

const { t } = useI18n();
const componentId = `automation-crm-task-${getCurrentInstance().uid}`;

const isRecord = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const validValueOr = (value, values, fallback) =>
  values.includes(value) ? value : fallback;

const normalizeDueIn = dueIn => {
  if (!isRecord(dueIn)) return undefined;

  const amount = Number(dueIn.amount);
  return {
    amount: Number.isFinite(amount) && amount > 0 ? Math.floor(amount) : 1,
    unit: validValueOr(dueIn.unit, DUE_IN_UNITS, 'days'),
  };
};

const normalizeCrmTask = modelValue => {
  const rawTask = Array.isArray(modelValue) ? modelValue[0] : modelValue;
  const task = isRecord(rawTask) ? rawTask : {};
  const rawContext = isRecord(task.context) ? task.context : {};
  const dueIn = normalizeDueIn(task.due_in);

  const normalizedTask = {
    title: typeof task.title === 'string' ? task.title : '',
    task_type: typeof task.task_type === 'string' ? task.task_type : '',
    idempotency_strategy: validValueOr(
      task.idempotency_strategy,
      IDEMPOTENCY_STRATEGIES,
      'per_execution'
    ),
    context: {
      source: validValueOr(rawContext.source, CONTEXT_SELECTORS, 'contact'),
      contact: validValueOr(rawContext.contact, CONTEXT_SELECTORS, 'contact'),
      conversation: Boolean(rawContext.conversation),
      visit: Boolean(rawContext.visit),
      offer: Boolean(rawContext.offer),
    },
  };

  if (PRIORITIES.includes(task.priority)) {
    normalizedTask.priority = task.priority;
  }

  if (WAITING_ON_VALUES.includes(task.waiting_on)) {
    normalizedTask.waiting_on = task.waiting_on;
  }

  if (dueIn) {
    normalizedTask.due_in = dueIn;
  }

  return normalizedTask;
};

const task = computed(() => normalizeCrmTask(props.modelValue));
const fieldId = field => `${componentId}-${field}`;

watch(
  () => props.modelValue,
  modelValue => {
    const normalizedValue = [normalizeCrmTask(modelValue)];

    if (JSON.stringify(modelValue) !== JSON.stringify(normalizedValue)) {
      emit('update:modelValue', normalizedValue);
    }
  },
  { immediate: true }
);

const priorityOptions = computed(() => [
  { value: 'low', label: t('AUTOMATION.ACTION.CRM_TASK.PRIORITY_LOW') },
  {
    value: 'normal',
    label: t('AUTOMATION.ACTION.CRM_TASK.PRIORITY_NORMAL'),
  },
  { value: 'high', label: t('AUTOMATION.ACTION.CRM_TASK.PRIORITY_HIGH') },
  {
    value: 'urgent',
    label: t('AUTOMATION.ACTION.CRM_TASK.PRIORITY_URGENT'),
  },
]);

const waitingOnOptions = computed(() => [
  {
    value: 'agent',
    label: t('AUTOMATION.ACTION.CRM_TASK.WAITING_ON_AGENT'),
  },
  {
    value: 'customer',
    label: t('AUTOMATION.ACTION.CRM_TASK.WAITING_ON_CUSTOMER'),
  },
  {
    value: 'partner',
    label: t('AUTOMATION.ACTION.CRM_TASK.WAITING_ON_PARTNER'),
  },
  {
    value: 'system',
    label: t('AUTOMATION.ACTION.CRM_TASK.WAITING_ON_SYSTEM'),
  },
  {
    value: 'none',
    label: t('AUTOMATION.ACTION.CRM_TASK.WAITING_ON_NONE'),
  },
]);

const dueInUnitOptions = computed(() => [
  {
    value: 'minutes',
    label: t('AUTOMATION.ACTION.CRM_TASK.DUE_IN_MINUTES'),
  },
  {
    value: 'hours',
    label: t('AUTOMATION.ACTION.CRM_TASK.DUE_IN_HOURS'),
  },
  { value: 'days', label: t('AUTOMATION.ACTION.CRM_TASK.DUE_IN_DAYS') },
  {
    value: 'weeks',
    label: t('AUTOMATION.ACTION.CRM_TASK.DUE_IN_WEEKS'),
  },
]);

const idempotencyOptions = computed(() => [
  {
    value: 'per_execution',
    label: t('AUTOMATION.ACTION.CRM_TASK.IDEMPOTENCY_PER_EXECUTION'),
  },
  {
    value: 'per_source',
    label: t('AUTOMATION.ACTION.CRM_TASK.IDEMPOTENCY_PER_SOURCE'),
  },
  {
    value: 'per_contact',
    label: t('AUTOMATION.ACTION.CRM_TASK.IDEMPOTENCY_PER_CONTACT'),
  },
  {
    value: 'per_conversation',
    label: t('AUTOMATION.ACTION.CRM_TASK.IDEMPOTENCY_PER_CONVERSATION'),
  },
  {
    value: 'per_visit',
    label: t('AUTOMATION.ACTION.CRM_TASK.IDEMPOTENCY_PER_VISIT'),
  },
  {
    value: 'per_offer',
    label: t('AUTOMATION.ACTION.CRM_TASK.IDEMPOTENCY_PER_OFFER'),
  },
]);

const contextSelectorOptions = computed(() => [
  {
    value: 'contact',
    label: t('AUTOMATION.ACTION.CRM_TASK.CONTEXT_CONTACT'),
  },
  {
    value: 'conversation',
    label: t('AUTOMATION.ACTION.CRM_TASK.CONTEXT_CONVERSATION'),
  },
  { value: 'visit', label: t('AUTOMATION.ACTION.CRM_TASK.CONTEXT_VISIT') },
  { value: 'offer', label: t('AUTOMATION.ACTION.CRM_TASK.CONTEXT_OFFER') },
]);

const updateTask = patch => {
  emit('update:modelValue', [normalizeCrmTask({ ...task.value, ...patch })]);
};

const updateContext = (field, value) => {
  updateTask({
    context: {
      ...task.value.context,
      [field]: value,
    },
  });
};

const updateDueIn = patch => {
  updateTask({
    due_in: {
      ...task.value.due_in,
      ...patch,
    },
  });
};

const toggleDueIn = event => {
  updateTask({
    due_in: event.target.checked ? { amount: 1, unit: 'days' } : undefined,
  });
};
</script>

<template>
  <div class="grid w-full grid-cols-1 gap-3 pt-1 md:grid-cols-2">
    <div class="flex min-w-0 flex-col gap-1 md:col-span-2">
      <label
        :for="fieldId('title')"
        class="text-sm font-medium text-n-slate-12"
      >
        {{ t('AUTOMATION.ACTION.CRM_TASK.TITLE_LABEL') }}
      </label>
      <input
        :id="fieldId('title')"
        data-test-id="automation-crm-task-title"
        :value="task.title"
        type="text"
        autocomplete="off"
        class="mb-0 h-9 w-full rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak outline-offset-[-1px] transition-all placeholder:text-n-slate-10 hover:outline-n-slate-6 focus:outline-n-brand dark:outline-n-strong"
        :placeholder="t('AUTOMATION.ACTION.CRM_TASK.TITLE_PLACEHOLDER')"
        @input="updateTask({ title: $event.target.value })"
      />
    </div>

    <div class="flex min-w-0 flex-col gap-1">
      <label
        :for="fieldId('task-type')"
        class="text-sm font-medium text-n-slate-12"
      >
        {{ t('AUTOMATION.ACTION.CRM_TASK.TASK_TYPE_LABEL') }}
      </label>
      <input
        :id="fieldId('task-type')"
        data-test-id="automation-crm-task-type"
        :value="task.task_type"
        type="text"
        autocomplete="off"
        class="mb-0 h-9 w-full rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak outline-offset-[-1px] transition-all placeholder:text-n-slate-10 hover:outline-n-slate-6 focus:outline-n-brand dark:outline-n-strong"
        :placeholder="t('AUTOMATION.ACTION.CRM_TASK.TASK_TYPE_PLACEHOLDER')"
        @input="updateTask({ task_type: $event.target.value })"
      />
    </div>

    <div class="flex min-w-0 flex-col gap-1">
      <label
        :for="fieldId('priority')"
        class="text-sm font-medium text-n-slate-12"
      >
        {{ t('AUTOMATION.ACTION.CRM_TASK.PRIORITY_LABEL') }}
      </label>
      <select
        :id="fieldId('priority')"
        data-test-id="automation-crm-task-priority"
        :value="task.priority || ''"
        class="mb-0 h-9 w-full rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak outline-offset-[-1px] transition-all hover:outline-n-slate-6 focus:outline-n-brand dark:outline-n-strong"
        @change="updateTask({ priority: $event.target.value || undefined })"
      >
        <option value="">
          {{ t('AUTOMATION.ACTION.CRM_TASK.OPTIONAL') }}
        </option>
        <option
          v-for="option in priorityOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <div class="flex min-w-0 flex-col gap-1">
      <label
        :for="fieldId('waiting-on')"
        class="text-sm font-medium text-n-slate-12"
      >
        {{ t('AUTOMATION.ACTION.CRM_TASK.WAITING_ON_LABEL') }}
      </label>
      <select
        :id="fieldId('waiting-on')"
        data-test-id="automation-crm-task-waiting-on"
        :value="task.waiting_on || ''"
        class="mb-0 h-9 w-full rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak outline-offset-[-1px] transition-all hover:outline-n-slate-6 focus:outline-n-brand dark:outline-n-strong"
        @change="updateTask({ waiting_on: $event.target.value || undefined })"
      >
        <option value="">
          {{ t('AUTOMATION.ACTION.CRM_TASK.OPTIONAL') }}
        </option>
        <option
          v-for="option in waitingOnOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <fieldset
      class="grid min-w-0 grid-cols-1 gap-2 rounded-lg p-3 outline outline-1 outline-n-weak dark:outline-n-strong md:col-span-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
    >
      <legend class="px-1 text-sm font-medium text-n-slate-12">
        {{ t('AUTOMATION.ACTION.CRM_TASK.DUE_IN_LABEL') }}
      </legend>
      <label
        class="flex items-center gap-2 text-sm text-n-slate-12 md:col-span-2"
      >
        <input
          data-test-id="automation-crm-task-due-in-enabled"
          :checked="Boolean(task.due_in)"
          type="checkbox"
          class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
          @change="toggleDueIn"
        />
        {{ t('AUTOMATION.ACTION.CRM_TASK.DUE_IN_ENABLED') }}
      </label>
      <template v-if="task.due_in">
        <div class="flex min-w-0 flex-col gap-1">
          <label
            :for="fieldId('due-in-amount')"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ t('AUTOMATION.ACTION.CRM_TASK.DUE_IN_AMOUNT_LABEL') }}
          </label>
          <input
            :id="fieldId('due-in-amount')"
            data-test-id="automation-crm-task-due-in-amount"
            :value="task.due_in.amount"
            type="number"
            min="1"
            step="1"
            class="mb-0 h-9 w-full rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak outline-offset-[-1px] transition-all hover:outline-n-slate-6 focus:outline-n-brand dark:outline-n-strong"
            @input="updateDueIn({ amount: $event.target.value })"
          />
        </div>
        <div class="flex min-w-0 flex-col gap-1">
          <label
            :for="fieldId('due-in-unit')"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ t('AUTOMATION.ACTION.CRM_TASK.DUE_IN_UNIT_LABEL') }}
          </label>
          <select
            :id="fieldId('due-in-unit')"
            data-test-id="automation-crm-task-due-in-unit"
            :value="task.due_in.unit"
            class="mb-0 h-9 w-full rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak outline-offset-[-1px] transition-all hover:outline-n-slate-6 focus:outline-n-brand dark:outline-n-strong"
            @change="updateDueIn({ unit: $event.target.value })"
          >
            <option
              v-for="option in dueInUnitOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </template>
    </fieldset>

    <div class="flex min-w-0 flex-col gap-1 md:col-span-2">
      <label
        :for="fieldId('idempotency')"
        class="text-sm font-medium text-n-slate-12"
      >
        {{ t('AUTOMATION.ACTION.CRM_TASK.IDEMPOTENCY_LABEL') }}
      </label>
      <select
        :id="fieldId('idempotency')"
        data-test-id="automation-crm-task-idempotency"
        :value="task.idempotency_strategy"
        class="mb-0 h-9 w-full rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak outline-offset-[-1px] transition-all hover:outline-n-slate-6 focus:outline-n-brand dark:outline-n-strong"
        @change="updateTask({ idempotency_strategy: $event.target.value })"
      >
        <option
          v-for="option in idempotencyOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <fieldset
      class="grid min-w-0 grid-cols-1 gap-3 rounded-lg p-3 outline outline-1 outline-n-weak dark:outline-n-strong md:col-span-2 md:grid-cols-2"
    >
      <legend class="px-1 text-sm font-medium text-n-slate-12">
        {{ t('AUTOMATION.ACTION.CRM_TASK.CONTEXT_LABEL') }}
      </legend>
      <div class="flex min-w-0 flex-col gap-1">
        <label
          :for="fieldId('context-source')"
          class="text-sm font-medium text-n-slate-12"
        >
          {{ t('AUTOMATION.ACTION.CRM_TASK.CONTEXT_SOURCE_LABEL') }}
        </label>
        <select
          :id="fieldId('context-source')"
          data-test-id="automation-crm-task-context-source"
          :value="task.context.source"
          class="mb-0 h-9 w-full rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak outline-offset-[-1px] transition-all hover:outline-n-slate-6 focus:outline-n-brand dark:outline-n-strong"
          @change="updateContext('source', $event.target.value)"
        >
          <option
            v-for="option in contextSelectorOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <div class="flex min-w-0 flex-col gap-1">
        <label
          :for="fieldId('context-contact')"
          class="text-sm font-medium text-n-slate-12"
        >
          {{ t('AUTOMATION.ACTION.CRM_TASK.CONTEXT_CONTACT_LABEL') }}
        </label>
        <select
          :id="fieldId('context-contact')"
          data-test-id="automation-crm-task-context-contact"
          :value="task.context.contact"
          class="mb-0 h-9 w-full rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak outline-offset-[-1px] transition-all hover:outline-n-slate-6 focus:outline-n-brand dark:outline-n-strong"
          @change="updateContext('contact', $event.target.value)"
        >
          <option
            v-for="option in contextSelectorOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <div class="flex flex-wrap gap-x-4 gap-y-2 md:col-span-2">
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input
            data-test-id="automation-crm-task-context-conversation"
            :checked="task.context.conversation"
            type="checkbox"
            class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
            @change="updateContext('conversation', $event.target.checked)"
          />
          {{ t('AUTOMATION.ACTION.CRM_TASK.CONTEXT_CONVERSATION_LABEL') }}
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input
            data-test-id="automation-crm-task-context-visit"
            :checked="task.context.visit"
            type="checkbox"
            class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
            @change="updateContext('visit', $event.target.checked)"
          />
          {{ t('AUTOMATION.ACTION.CRM_TASK.CONTEXT_VISIT_LABEL') }}
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input
            data-test-id="automation-crm-task-context-offer"
            :checked="task.context.offer"
            type="checkbox"
            class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
            @change="updateContext('offer', $event.target.checked)"
          />
          {{ t('AUTOMATION.ACTION.CRM_TASK.CONTEXT_OFFER_LABEL') }}
        </label>
      </div>
    </fieldset>
  </div>
</template>
